import { PINNED_KPI_NAME, type KpiCatalogEntry, type UserKpiEntry } from "@nexus/contracts";
import { sortUserKpis } from "./user-kpi-model";

/**
 * Adapt a `UserKpiEntry` to the `KpiCatalogEntry` shape the existing catalog
 * builders accept, so format, colour and decimals keep a single derivation path
 * rather than a second copy that drifts.
 *
 * `hidden` inverts to `visible` because the builders filter on the org flag.
 */
export function userKpiToCatalogEntry(entry: UserKpiEntry): KpiCatalogEntry {
  return {
    id: entry.id,
    name: entry.name,
    displayName: entry.displayName,
    formula: entry.formula,
    format: entry.format,
    polarity: entry.polarity,
    decimals: entry.decimals,
    index: entry.index,
    colorToken: entry.colorToken,
    visible: !entry.hidden,
    isCustom: entry.isCustom,
    isOverride: false,
    isPinned: entry.name === PINNED_KPI_NAME,
  };
}

/**
 * The rows a page-level picker should offer: the caller's own KPIs in their
 * persisted drag order, hidden ones dropped.
 *
 * Spend is force-included even when hidden. It is `LOCKED_CHART_METRIC_ID` on the
 * Overview chart and is always injected into `chartMetrics` by
 * `normalizeChartMetrics`, so dropping it would leave the chart with no primary
 * metric to order topics by.
 */
export function selectorRows(entries: UserKpiEntry[]): KpiCatalogEntry[] {
  return sortUserKpis(entries)
    .filter((entry) => !entry.hidden || entry.name === PINNED_KPI_NAME)
    .map(userKpiToCatalogEntry);
}

/**
 * Merge two by-id lookups, user winning on a name collision; org fills ids the
 * user list does not cover.
 *
 * The option list comes from the user's KPIs but rendering resolves through this
 * map, and a user-created KPI has no org row. Without the union, selecting one
 * would hit an undefined lookup and both `MyBrandKpiPillRow` and `resolveMetrics`
 * silently drop it.
 */
export function mergeById<T>(
  org: Record<string, T>,
  user: Record<string, T>,
): Record<string, T> {
  return { ...org, ...user };
}
